/**
 * 権限フラグの導出ロジック
 * #2133 設計 §3: 権限の判定は ID トークンの Custom Claims（role・companyId・
 * studioId）だけで行う。Firestore の `users.role` や `isAdmin` では判定しない
 * （darshana #2405 と同じ考え方）。判定できないときは見せない（fail-closed）。
 *
 * role の値は firebase/functions/src/temp_users/validation.ts の VALID_ROLES /
 * firebase/firestore.rules の isValidUserRole() と同じ集合にすること。
 *
 * このファイルは Firebase SDK に依存しない純粋関数のみを置く。
 */

/** users.role として有効な値 */
export const VALID_ROLES = [
  'admin',
  'companyAssociate',
  'studioAssociate',
  'instructor',
  'student',
] as const;

export type Role = (typeof VALID_ROLES)[number];

/** ID トークンから読み取る custom claim */
export interface RoleClaims {
  role: Role | null;
  companyId: string | null;
  studioId: string | null;
}

/** claim 取得前・取得失敗時・権限なしの安全側（fail-closed）の既定値 */
export const NO_ROLE_CLAIMS: RoleClaims = {
  role: null,
  companyId: null,
  studioId: null,
};

export interface RoleFlags {
  role: Role | null;
  companyId: string | null;
  studioId: string | null;
  isAdmin: boolean;
  isCompanyAssociate: boolean;
  isStudioAssociate: boolean;
  isInstructor: boolean;
  isStudent: boolean;
  /** #2133 設計 §3: admin・companyAssociate・studioAssociate だけがこの Web を使える */
  canUseWeb: boolean;
}

function isValidRole(value: unknown): value is Role {
  return typeof value === 'string' && (VALID_ROLES as readonly string[]).includes(value);
}

/**
 * Firebase の `IdTokenResult.claims`（任意の形の object）から RoleClaims を
 * 安全に取り出す。値が期待する形以外（未設定・不正な型等）は null 扱いにする
 * （fail-closed）。
 */
export function extractRoleClaims(
  tokenClaims: Record<string, unknown> | null | undefined
): RoleClaims {
  const role = isValidRole(tokenClaims?.role) ? tokenClaims.role : null;
  const companyId = typeof tokenClaims?.companyId === 'string' && tokenClaims.companyId !== ''
    ? tokenClaims.companyId
    : null;
  const studioId = typeof tokenClaims?.studioId === 'string' && tokenClaims.studioId !== ''
    ? tokenClaims.studioId
    : null;

  // role が無ければ companyId/studioId も権限判定には使わせない（fail-closed）。
  if (role === null) {
    return NO_ROLE_CLAIMS;
  }

  return { role, companyId, studioId };
}

/**
 * custom claim から権限フラグを導出する。
 * claims が null（未取得・未ログイン・取得失敗）の場合は全て false（fail-closed）。
 */
export function deriveRoleFlags(claims: RoleClaims | null): RoleFlags {
  const role = claims?.role ?? null;
  const isAdmin = role === 'admin';
  const isCompanyAssociate = role === 'companyAssociate';
  const isStudioAssociate = role === 'studioAssociate';
  const isInstructor = role === 'instructor';
  const isStudent = role === 'student';

  return {
    role,
    companyId: claims?.companyId ?? null,
    studioId: claims?.studioId ?? null,
    isAdmin,
    isCompanyAssociate,
    isStudioAssociate,
    isInstructor,
    isStudent,
    canUseWeb: isAdmin || isCompanyAssociate || isStudioAssociate,
  };
}
